<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/EntryComponents.vue (site grouping, dots menu).
  Dropped: Export file (server). Rename / Edit selection / Delete became store actions.
-->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import Menu from 'primevue/menu';
import Button from 'primevue/button';
import type { MenuItem } from 'primevue/menuitem';
import { useLiteStore, visualizationName, type Component, type FilterModel } from '@nanome-lite/state';
import { useConfirmMenuItem } from '@/composables/confirmMenuItem';
import ComponentRow from './ComponentRow.vue';
import ComponentVisualizations from './ComponentVisualizations.vue';
import RenamePrompt from './RenamePrompt.vue';

const props = defineProps<{ entrySerial: number | null; entryName: string }>();

const store = useLiteStore();

const collapsedSites = ref<Record<string, boolean>>({});
const ctxComponent = ref<Component | null>(null);
const ctxComponentEl = ref<EventTarget | null>(null);
const menuActionsComponent = ref<InstanceType<typeof Menu>>();
const panelRename = ref<InstanceType<typeof RenamePrompt>>();
const panelVisualizations = ref<InstanceType<typeof ComponentVisualizations>>();

const { menuItem: deleteMenuItem } = useConfirmMenuItem({
  menu: menuActionsComponent,
  onConfirm() { if (ctxComponent.value) store.deleteComponent(ctxComponent.value.serial); },
});

const menuItemsComponent = computed<MenuItem[]>(() => [
  { label: 'Rename', icon: 'i-tabler-pencil', command: () => togglePanelRename() },
  { label: 'Edit selection', icon: 'i-tabler-mist', command: () => { if (ctxComponent.value) store.selectComponent(ctxComponent.value.serial); } },
  deleteMenuItem.value,
]);

const components = computed(() => props.entrySerial != null ? store.componentsForEntry(props.entrySerial) : store.crossEntryComponents);

type Group = { type: 'component'; component: Component } | { type: 'site'; key: string; name: string; components: Component[] };

const groupedComponents = computed<Group[]>(() => {
  const ungrouped: Component[] = [];
  const siteMap = new Map<string, Component[]>();
  for (const component of components.value) {
    const info = visitFilters(component.filters);
    if (!info.isSite) { ungrouped.push(component); continue; }
    const key = `${info.chainName}:${info.residueSerial}`;
    if (!siteMap.has(key)) siteMap.set(key, []);
    siteMap.get(key)!.push(component);
  }
  const result: Group[] = ungrouped.map(component => ({ type: 'component', component }));
  for (const [key, siteComponents] of siteMap) {
    if (siteComponents.length === 1) result.push({ type: 'component', component: siteComponents[0]! });
    else result.push({ type: 'site', key, name: getSiteName(siteComponents), components: siteComponents });
  }
  return result;
});

watch(components, () => {
  if (!ctxComponent.value) return;
  ctxComponent.value = components.value.find(c => c.serial === ctxComponent.value!.serial) ?? null;
}, { deep: true });

const prevAllHidden = new Map<string, boolean>();
watch(groupedComponents, (items) => {
  const seen = new Set<string>();
  for (const item of items) {
    if (item.type !== 'site') continue;
    seen.add(item.key);
    const allHidden = item.components.every(c => c.hidden);
    const wasAllHidden = prevAllHidden.get(item.key);
    if (allHidden && !wasAllHidden) collapsedSites.value[item.key] = true;
    else if (!allHidden && wasAllHidden) delete collapsedSites.value[item.key];
    prevAllHidden.set(item.key, allHidden);
  }
  for (const key of prevAllHidden.keys()) if (!seen.has(key)) prevAllHidden.delete(key);
}, { deep: true, immediate: true });

function getComponentVisualizationNames(component: Component) {
  const visualizations = component.visualizations.filter(v => v.visible);
  if (!visualizations.length) return 'No representations';
  if (visualizations.length > 1) return `${visualizations.length} representations`;
  return visualizationName(visualizations[0]!);
}

function getSiteEyeIcon(cs: Component[]) {
  if (cs.every(c => c.hidden)) return 'i-tabler-eye-off';
  if (cs.some(c => c.hidden)) return 'i-tabler-eye-minus';
  return 'i-tabler-eye';
}

function getSiteName(siteComponents: Component[]) {
  const names = siteComponents.map(c => c.name);
  let suffix = names[0] ?? '';
  for (const name of names.slice(1)) while (suffix && !name.endsWith(suffix)) suffix = suffix.slice(1);
  return suffix ? `Site ${suffix.trim()}` : 'Site';
}

function renameComponent(value: string) { if (ctxComponent.value) store.renameComponent(ctxComponent.value.serial, value); }
function toggleComponent(serial: number, hidden: boolean) { store.toggleHidden([serial], hidden); }
function toggleSiteComponents(cs: Component[]) { store.toggleHidden(cs.map(c => c.serial), !cs.every(c => c.hidden)); }
function onSiteRowClick(e: MouseEvent, cs: Component[]) {
  if ((e.target as HTMLElement).closest('button')) return;
  store.toggleComponentsSelection(cs.map(c => c.serial));
}
function toggleCollapsedSite(key: string) {
  if (collapsedSites.value[key]) delete collapsedSites.value[key];
  else collapsedSites.value[key] = true;
}

async function toggleMenuActions(e: Event, component: Component) {
  menuActionsComponent.value?.hide();
  panelRename.value?.hide();
  if (ctxComponentEl.value === e.currentTarget) return;
  await nextTick();
  ctxComponentEl.value = e.currentTarget;
  ctxComponent.value = component;
  menuActionsComponent.value?.show(e);
}

async function togglePanelVisualizations(e: Event, component: Component) {
  panelVisualizations.value?.hide();
  if (ctxComponentEl.value === e.currentTarget) return;
  await nextTick();
  ctxComponentEl.value = e.currentTarget;
  ctxComponent.value = component;
  panelVisualizations.value?.show(e);
}

function togglePanelRename() {
  if (!ctxComponent.value) return;
  const currentTarget = ctxComponentEl.value;
  const name = ctxComponent.value.name;
  menuActionsComponent.value?.hide();
  setTimeout(() => panelRename.value?.show(currentTarget, name));
}

/** EntryComponents.vue:962-1008 — a "site" is a per-residue component (entry + model + single chain + single residue). */
function visitFilters(filters: FilterModel[]) {
  const result = { entrySerial: null as number | null, isCurrentModel: false, modelSerial: null as number | null, chainName: null as string | null, residueSerial: null as number | null, isSite: false };
  const visit = (items: FilterModel[]) => {
    for (const filter of items) {
      switch (filter.kind) {
        case 'EntrySerial': result.entrySerial = filter.entrySerial ?? null; break;
        case 'CurrentModel': result.isCurrentModel = true; break;
        case 'ModelSerials': result.modelSerial = filter.serialRangeSet?.sets[0] ?? filter.serialRangeSet?.ranges[0]?.[0] ?? null; break;
        case 'ChainNames': if (filter.names?.length === 1) result.chainName = filter.names[0] ?? null; break;
        case 'Residues': if (filter.residues?.length === 1) result.residueSerial = filter.residues[0]?.residueSerial ?? null; break;
        case 'ResidueSerials': if (filter.serialRangeSet?.sets.length === 1 && !filter.serialRangeSet.ranges.length) result.residueSerial = filter.serialRangeSet.sets[0] ?? null; break;
      }
      if (filter.containedFilters) visit(filter.containedFilters);
    }
  };
  visit(filters);
  result.isSite = result.entrySerial != null && (result.isCurrentModel || result.modelSerial != null) && result.chainName != null && result.residueSerial != null;
  return result;
}
</script>

<template>
  <div class="flex flex-col">
    <div v-if="components.length === 0" class="op-50 text-sm pl-14 py-1">
      No representations yet
    </div>

    <template v-for="item in groupedComponents" :key="item.type === 'site' ? item.key : item.component.serial">
      <ComponentRow
        v-if="item.type === 'component'"
        :component="item.component"
        :visualization-label="getComponentVisualizationNames(item.component)"
        class="pl-14"
        @actions="toggleMenuActions"
        @toggle="toggleComponent"
        @visualizations="togglePanelVisualizations"
      />

      <div v-else class="flex flex-col">
        <div
          :data-selection="store.componentSelectionState(item.components.map(c => c.serial))"
          class="selectable-row flex items-center gap-2 pl-14 pr-4 py-1"
          @click="onSiteRowClick($event, item.components)"
        >
          <Button
            :icon="collapsedSites[item.key] ? 'i-tabler-chevron-right' : 'i-tabler-chevron-down'"
            severity="secondary"
            size="small"
            rounded
            @click="toggleCollapsedSite(item.key)"
          />
          <Button :icon="getSiteEyeIcon(item.components)" severity="secondary" size="small" rounded @click="toggleSiteComponents(item.components)" />
          <span :class="{ 'op-25': item.components.every(c => c.hidden) }" class="flex-1 truncate">
            {{ item.name }}
          </span>
        </div>
        <div v-if="!collapsedSites[item.key]" class="flex flex-col">
          <ComponentRow
            v-for="component in item.components"
            :key="component.serial"
            :component="component"
            :visualization-label="getComponentVisualizationNames(component)"
            class="pl-24"
            @actions="toggleMenuActions"
            @toggle="toggleComponent"
            @visualizations="togglePanelVisualizations"
          />
        </div>
      </div>
    </template>
  </div>

  <Menu ref="menuActionsComponent" :model="menuItemsComponent" popup @hide="ctxComponentEl = null" />
  <RenamePrompt ref="panelRename" type="component" @rename="renameComponent" />
  <ComponentVisualizations ref="panelVisualizations" :component="ctxComponent" @hide="ctxComponentEl = null" />
</template>
